import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import api from "@/lib/axios"
import { getListParentKomponenPenilaian } from "@/services/getData"
import {
  BobotKomponenKaryatulis,
  Option,
  ParentKomponenPenilaian,
} from "@/types"
import { AxiosError } from "axios"
import { Loader2 } from "lucide-react"
import { useState } from "react"
import toast from "react-hot-toast"
import { FaPlus } from "react-icons/fa6"
import { MdModeEdit } from "react-icons/md"
import { RiDeleteBack2Line } from "react-icons/ri"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

const postData = async (newData: BobotKomponenKaryatulis) => {
  const response = await api.post(
    "/operator-pasca/komponen-penilaian/bobot-komponen-karyatulis-store",
    newData
  )
  return response.data
}

const patchData = async (newData: BobotKomponenKaryatulis) => {
  const response = await api.patch(
    "/operator-pasca/komponen-penilaian/bobot-komponen-karyatulis-update",
    newData
  )
  return response.data
}

const deleteData = async (deletedData: {
  id_ref_bobot_komponen_karyatulis_pasca: string
}) => {
  const response = await api({
    url: "/operator-pasca/komponen-penilaian/bobot-komponen-karyatulis-delete",
    method: "DELETE",
    data: deletedData,
  })
  return response.data
}

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE"
  selectedRow?: BobotKomponenKaryatulis | null
  closeModal: () => void
}) {
  const [form, setForm] = useState<BobotKomponenKaryatulis>({
    id_ref_bobot_komponen_karyatulis_pasca:
      selectedRow?.id_ref_bobot_komponen_karyatulis_pasca || "",
    id_ref_parent_komponen_penilaian_pasca:
      selectedRow?.id_ref_parent_komponen_penilaian_pasca || "",
    bobot: selectedRow?.bobot || 0,
    tingkat: selectedRow?.tingkat || "",
  })

  const isPost = method === "POST"
  const isPatch = method === "PATCH"
  const isDelete = method === "DELETE"

  const { data: listParentKomponen } = useQuery({
    queryKey: ["listParentKomponenPenilaian"],
    queryFn: getListParentKomponenPenilaian,
    select: (data) => {
      return data?.map((item: ParentKomponenPenilaian) => {
        return {
          label: `${item.kode}-${item.nama_ref_parent_komponen}`,
          value: `${item.id_ref_parent_komponen_penilaian_pasca}`,
        }
      })
    },
  })

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["listBobotKomponenKaryatulis"],
      })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete "
          description={`Apakah anda yakin akan menghapus "${""}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={isPost ? "Insert Data" : "Update Data"}
      />

      <div>
        <Label>Parent Komponen Penilaian</Label>
        <Select
          value={form.id_ref_parent_komponen_penilaian_pasca || ""}
          onValueChange={(value: string) => {
            setForm((prev) => ({
              ...prev,
              id_ref_parent_komponen_penilaian_pasca: `${value}`,
            }))
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder={"Pilih Parent Komponen"} />
          </SelectTrigger>
          <SelectContent>
            {listParentKomponen?.map((option: Option, index: number) => (
              <SelectItem key={index} value={`${option.value}`}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Tingkat</Label>
        <Input
          placeholder="Ketik tingkat..."
          value={form.tingkat}
          onChange={(event) => {
            setForm((prev) => ({
              ...prev,
              tingkat: event.target.value,
            }))
          }}
        />
      </div>

      <div>
        <Label>Bobot</Label>
        <Input
          type="number"
          placeholder="Ketik bobot..."
          value={form.bobot}
          onChange={(event) => {
            setForm((prev) => ({ ...prev, bobot: Number(event.target.value) }))
          }}
        />
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
